// About named parameters via desctructuring with default values
// `func({ correction = false } = {})`, for example:
// http://exploringjs.com/es6/ch_parameter-handling.html#_named-parameters-via-destructuring

import GeoPoint from './geo_point';

export default class GeoPointInput extends GeoPoint {
	static get geocoder() {
		return this._geocoder || (this._geocoder = new google.maps.Geocoder());
	}

	constructor(visible_input) {
		super(visible_input);

		const self = this;
		self.visible_input = visible_input;
		self.forbidden = false;
		self.has_locality = false;
		self.onlyCountries = [];

		const onlyCountries = self.visible_input.dataset.onlyCountries;
		if (onlyCountries) {
			self.onlyCountries = JSON.parse(onlyCountries);
		}

		// Remember hidden input for coordinates if exists
		self.hidden_input = document.querySelector(
			`[name="${self.visible_input.name.replace('address', 'coordinates')}"]`
		);

		// Metadata input
		self.metadata_input = document.querySelector(
			`[name="${self.visible_input.name.replace('address', 'metadata')}"]`
		);

		// Init Google Autocomplete
		self.autocomplete = new google.maps.places.Autocomplete(
			self.visible_input, { fields: ['place_id', 'name', 'types'] }
		);

		google.maps.event.addDomListener(self.visible_input, 'keydown', event => {
			if (event.keyCode === 13) {
				// Prevent submitting form by Enter key in Autocomplete inputs
				event.preventDefault();

				if (self.hidden_input) {
					// Show Transport Type Prices
					self._update_coordinates({ force: true }, () => {
						self.visible_input.dispatchEvent(new Event('change'));
					});
				}
			}
		});

		// Fill in hidden input with data from Google Geocoder if exists
		if (self.hidden_input) {
			// When address is filled in (and focus on input is lost)
			self.visible_input.addEventListener('blur', () => {
				// HACK for Safari Webkit (click on suggestion)
				setTimeout(() => self._update_coordinates({ force: true }), 100);
			});

			// Fill in hidden input on initialization (if visible input has value)
			if (self.visible_input.value) {
				self._update_coordinates();
			}
		}

		self.visible_input.addEventListener('countryUpdated', () => {
			const vat_input = document.querySelector('.carrier_vat');
			if (vat_input) {
				vat_input.toggleAttribute(
					'required',
					JSON
						.parse(vat_input.dataset.supportedCountries)
						.includes(this.country_iso)
				);
			}
		});
	}

	get address() {
		return this.visible_input.value;
	}

	get coordinates() {
		return this.hidden_input.value;
	}

	set coordinates(value) {
		if (this.hidden_input) this.hidden_input.value = value;
	}

	clear_inputs() {
		this.clear_update_timeouts();
		this.hidden_input.value = null;
		this.visible_input.value = null;
	}

	clear_update_timeouts() {
		clearTimeout(this._update_coordinates_timeout);
		clearTimeout(this._update_address_timeout);
	}

	need_validate() {
		return this.address;
	}

	fill_metadata(result) {
		if (!this.metadata_input) return;
		this.metadata_input.value = JSON.stringify(result);
	}

	update_address(coordinates, { correction = false } = {}) {
		// Stop retrying
		clearTimeout(this._update_address_timeout);

		if (this.hidden_input) this.hidden_input.value = coordinates;
		this.visible_input.value = null;

		// Don't make API request for `null` data
		if (!coordinates) return;

		//  Make API request by coordinates for address
		this.constructor.geocoder.geocode(
			{ location: coordinates },
			(results, status) => {
				// Retry if over query limit
				if (status == 'OVER_QUERY_LIMIT') {
					this._update_address_timeout = setTimeout(
						() => this.update_address(coordinates, { correction: correction }),
						500
					);
					return;
				}

				if (this.map && this.map.marker_dragging) return;

				// If input value was changed while request was carried out
				if (this.hidden_input && coordinates != this.hidden_input.value) return;

				if (status == 'OK') {
					// HACK: Google returns some results with "Unnamed Road"
					// for which doesn't know coordinates
					const filtered_results = results.filter(
						result => !result.formatted_address.includes('Unnamed Road')
					);
					const first_filtered_result = filtered_results[0];
					// Write response to metadata input value
					this.fill_metadata(results);
					if (!first_filtered_result) return;
					// Fill in visible input with address
					this.visible_input.value = first_filtered_result.formatted_address;
					this._update_coordinates({ correction: correction });
				}
			}
		);
	}

	valid() {
		this.errors = [];
		if (!this.need_validate()) return true;

		if (this.forbidden) this.errors.push('forbidden');
		if (
			!(
				this.forbidden ||
					this.has_locality && this.coordinates
			)
		) {
			this.errors.push('empty_locality');
		}

		if (
			this.onlyCountries.length > 0 &&
			!this.onlyCountries.includes(this.country_iso.toLowerCase())
		) {
			this.errors.push('outside_us_and_ca');
		}


		return (this.errors.length == 0);
	}

	validate() {
		this.valid();
		if (this.visible_input.classList.contains('entry_field__element')) {
			this.visible_input.classList.toggle(
				'entry_field__element--forbidden',
				this.errors.includes('forbidden')
			);
			this.visible_input.form.classList.toggle(
				'forbidden',
				this.visible_input
					.form
					.form_object
					.geo_points
					.some(point => point.visible_input.classList.contains(
						'entry_field__element--forbidden'
					))
			);
			this.visible_input.classList.toggle(
				'entry_field__element--empty_locality',
				this.errors.includes('empty_locality')
			);
			this.visible_input.form.classList.toggle(
				'empty_locality',
				this.visible_input
					.form
					.form_object
					.geo_points
					.some(point => point.visible_input.classList.contains(
						'entry_field__element--empty_locality'
					))
			);

			this.visible_input.classList.toggle(
				'entry_field__element--outside_us_and_ca',
				this.errors.includes('outside_us_and_ca')
			);

			this.visible_input.form.classList.toggle(
				'outside_us_and_ca',
				this.errors.includes('outside_us_and_ca')
			);
		} else {
			this.visible_input.parentElement.classList.toggle(
				'forbidden', this.errors.includes('forbidden')
			);
			
			this.visible_input.form.classList.toggle(
				'forbidden',
				this.errors.includes('forbidden')
			);

			this.visible_input.parentElement.classList.toggle(
				'empty_locality', this.errors.includes('empty_locality')
			);
			
			this.visible_input.form.classList.toggle(
				'empty_locality',
				this.errors.includes('empty_locality')
			);

		}
	}

	_update_coordinates(
		{ correction = false, force = false } = {}, callback = null
	) {
		const self = this;

		// Stop retrying
		clearTimeout(self._update_coordinates_timeout);

		const address = self.address;

		// Flag for form submitting and directions
		self.updating_coordinates = true;

		function finish() {
			// Set marker on Map
			self._update_marker();

			self.validate();

			// Reset flag
			self.updating_coordinates = false;

			// Update directions
			if (self.directions) {
				self.directions.update({ clean_before: !correction }, callback);
			} else {
				// Calling custom callback
				if (callback) callback();
			}

			self.visible_input.dispatchEvent(
				new CustomEvent('GeoPoint updated', { bubbles: true })
			);
		}

		// Check if address is empty
		if (!address) {
			self.coordinates = null;
			self.forbidden = false;
			finish();

			return;
		}

		// Make API request by address for coordinates
		self.constructor.geocoder.geocode(
			{ address: address },
			(results, status) => {
				// Retry if over query limit
				if (status == 'OVER_QUERY_LIMIT') {
					self._update_coordinates_timeout = setTimeout(
						() => self._update_coordinates({ correction: correction }),
						500
					);
					return;
				}

				if (self.map && self.map.marker_dragging && !force) return;

				// If input value was changed while request was carried out
				if (address != self.address) return;

				// If metadata empty fiiling it
				this.fill_metadata(results);

				if (results) {
					this.has_locality =
						results
							.flatMap(el => el.address_components.flatMap(c => c.types))
							.some(type => [
								'locality',
								'administrative_area_level_3'
							].includes(type));
				}

				// Add country attribute for custom clearance and fill in hidden input
				if (status == 'OK') {
					self.coordinates = results[0].geometry.location.toString();

					const country = results[0].address_components.find(
						component => component.types.includes('country')
					);
					if (country) {
						self.country = country.long_name;
						self.country_iso = country.short_name;
					} else {
						self.country = null;
						self.country_iso = null;
					}

					self.visible_input.dispatchEvent(new CustomEvent('countryUpdated'));
				} else {
					self.coordinates = null;
					self.country = null;
					self.country_iso = null;

				}

				const geo_point_params = new URLSearchParams();
				geo_point_params.set('coordinates', self.coordinates);

				fetch(`/api/v2/geo_points/check?${geo_point_params.toString()}`, {
					method: 'GET',
					credentials: 'same-origin'
				})
					.then(response => {
						if (response.ok) {
							return false;
						} else {
							throw response;
						}
					})
					.catch(response => {
						if (response.status == 422) {
							return true;
						} else {
							throw response;
						}
					})
					.then(result => {
						self.forbidden = result;
						finish();
					})
					.catch(response => finish());
			}
		);
	}
}
